"use client";

import { useCallback, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  DataGridPro,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { useCampaignDialogs } from "../Providers/CampaignDialogsContext";
import { CustomToolbar } from "./CustomToolbar";
import { columns } from "./columns";
import { FetchCampaignsV2Response } from "@/api/interfaces/responses/campaigns";

interface CampaignsDataGridProps {
  data: FetchCampaignsV2Response[];
  params: Record<string, string | string[] | undefined>;
  totalCount: number;
}

export default function CampaignsDataGrid({
  data,
  params,
  totalCount,
}: CampaignsDataGridProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { rowSelectionModel, setRowSelectionModel } = useCampaignDialogs();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: searchParams?.get("sort-by") || "name",
      sort: (searchParams?.get("sort-dir") as "asc" | "desc") || "asc",
    },
  ]);

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.set("page", (model.page + 1).toString());
      params.set("per-page", model.pageSize.toString());
      router.push(`/campaigns?${params.toString()}`);
    },
    [router, searchParams],
  );

  const paginationModel: GridPaginationModel = {
    page: (parseInt(params.page as string) || 1) - 1,
    pageSize: parseInt(params["per-page"] as string) || 10,
  };

  const onSortModelChange = useCallback(
    (newSortModel: GridSortModel) => {
      setSortModel(newSortModel);

      const params = new URLSearchParams(searchParams?.toString() || "");
      if (newSortModel.length > 0) {
        params.set("sort-by", newSortModel[0].field);
        params.set("sort-dir", newSortModel[0].sort || "asc");
      } else {
        params.delete("sort-by");
        params.delete("sort-dir");
      }
      router.push(`/campaigns?${params.toString()}`);
    },
    [router, searchParams],
  );

  return (
    <DataGridPro
      checkboxSelection
      columns={columns}
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      disableRowSelectionOnClick
      filterMode="server"
      onPaginationModelChange={onPaginationModelChange}
      onRowSelectionModelChange={setRowSelectionModel}
      onSortModelChange={onSortModelChange}
      pageSizeOptions={[10, 25, 50, 100]}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      rows={data}
      rowCount={totalCount}
      rowSelectionModel={rowSelectionModel}
      sortingMode="server"
      sortModel={sortModel}
      sortingOrder={["asc", "desc"]}
      slots={{
        toolbar: CustomToolbar,
      }}
      showToolbar
    />
  );
}
